/**
 * SignatureManager.jsx — Settings → Signature Manager (Phase 2, BW-06).
 *
 * Super-admin only. Edits the company-wide signature settings stored in
 * the `signature_globals` Mongo doc and read by every user's signature
 * renderer. Mirrors the existing MySignature spec exactly:
 *   • Live HTML preview on the right.
 *   • Inputs: company name, website URL/text, logo URL, 4 social URLs.
 *   • Save + Reset to defaults.
 *
 * The endpoint precedence is DB → env (SIG_*) → DEFAULTS, so a blank
 * field falls back to the env/default value rather than being stored
 * as empty. This matches how Phase 1 already behaved for per-user
 * signature fields and avoids the "I cleared a field and now my
 * signature is broken" trap.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Loader2, RotateCcw, Globe, Linkedin, Facebook, Youtube, Instagram, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_FIELDS = [
  { key: "linkedin",  label: "LinkedIn",  icon: Linkedin,  placeholder: "https://linkedin.com/company/your-handle" },
  { key: "facebook",  label: "Facebook",  icon: Facebook,  placeholder: "https://facebook.com/your-page" },
  { key: "youtube",   label: "YouTube",   icon: Youtube,   placeholder: "https://youtube.com/@your-channel" },
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/your-handle" },
];

export default function SignatureManager() {
  const token = useMemo(() => localStorage.getItem("token"), []);
  const [globals, setGlobals]   = useState(null);   // server-rendered settings
  const [form, setForm]         = useState({ company_name: "", website_url: "", website_text: "", logo_url: "" });
  const [social, setSocial]     = useState({ linkedin: "", facebook: "", youtube: "", instagram: "" });
  const [previewHtml, setPreviewHtml] = useState("");
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/signature/global`, { headers: { Authorization: `Bearer ${token}` } });
      const g = r.data || {};
      setGlobals(g);
      setForm({
        company_name: g.company_name || "",
        website_url:  g.website_url  || "",
        website_text: g.website_text || "",
        logo_url:     g.logo_url     || "",
      });
      setSocial({
        linkedin:  g.social?.linkedin  || "",
        facebook:  g.social?.facebook  || "",
        youtube:   g.social?.youtube   || "",
        instagram: g.social?.instagram || "",
      });
    } catch (err) {
      toast.error(`Failed to load globals — ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Pull the current super-admin's rendered preview — it picks up the
  // globals automatically since the backend merges them on render.
  const refreshPreview = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/signature/me/rendered`, { headers: { Authorization: `Bearer ${token}` } });
      setPreviewHtml(r.data?.html || "");
    } catch { /* noop */ }
  }, [token]);

  useEffect(() => { load(); refreshPreview(); }, [load, refreshPreview]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, social };
      const r = await axios.patch(`${API}/signature/global`, body, { headers: { Authorization: `Bearer ${token}` } });
      setGlobals(r.data || null);
      toast.success("Company signature updated — applies to every user immediately");
      await refreshPreview();
    } catch (err) {
      toast.error(`Save failed — ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Clear the customised global signature settings and fall back to environment defaults?")) return;
    setResetting(true);
    try {
      // Server PATCH treats empty string as "fall back to env/default".
      await axios.patch(`${API}/signature/global`, {
        company_name: "", website_url: "", website_text: "", logo_url: "",
        social: { linkedin: "", facebook: "", youtube: "", instagram: "" },
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Reset to defaults");
      await load();
      await refreshPreview();
    } catch (err) {
      toast.error(`Reset failed — ${err?.response?.data?.detail || err.message}`);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="signature-manager-loading" style={{ display: "flex", alignItems: "center", gap: 8, color: "#666" }}>
        <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} /> Loading global settings…
      </div>
    );
  }

  return (
    <div data-testid="signature-manager" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
      {/* ── LEFT: form ── */}
      <div>
        <div style={{ marginBottom: 14 }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Signature Manager</h2>
          <p style={{ fontSize: 12, color: "#666", margin: "4px 0 0" }}>
            Company-wide signature settings. Changes apply to every user's signature instantly.
            {globals?.customised ? (
              <span style={{ marginLeft: 6, color: "#0F7A47", fontWeight: 500 }}>● Customised</span>
            ) : (
              <span style={{ marginLeft: 6, color: "#888" }}>● Using environment defaults</span>
            )}
          </p>
        </div>

        <Field label="Company name" testId="sigmgr-company">
          <input
            data-testid="sigmgr-company-input"
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            placeholder="Enable Healthcare"
            className="input"
            style={INPUT}
          />
        </Field>

        <Field label="Website URL" testId="sigmgr-website-url">
          <div style={{ position: "relative" }}>
            <Globe style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "#999" }} />
            <input
              data-testid="sigmgr-website-url-input"
              value={form.website_url}
              onChange={(e) => setForm({ ...form, website_url: e.target.value })}
              placeholder="https://www.example.com"
              className="input"
              style={{ ...INPUT, paddingLeft: 32 }}
            />
          </div>
        </Field>

        <Field label="Website display text" testId="sigmgr-website-text">
          <input
            data-testid="sigmgr-website-text-input"
            value={form.website_text}
            onChange={(e) => setForm({ ...form, website_text: e.target.value })}
            placeholder="www.example.com"
            className="input"
            style={INPUT}
          />
        </Field>

        <Field label="Logo URL" testId="sigmgr-logo-url" hint="PNG/JPG, max-height 28px in signatures. Leave blank to hide.">
          <div style={{ position: "relative" }}>
            <ImageIcon style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "#999" }} />
            <input
              data-testid="sigmgr-logo-url-input"
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="https://cdn.example.com/logo.png"
              className="input"
              style={{ ...INPUT, paddingLeft: 32 }}
            />
          </div>
        </Field>

        <div style={{ marginTop: 22, marginBottom: 8, fontSize: 12, color: "#666", textTransform: "uppercase", letterSpacing: 0.4 }}>Social links</div>
        {SOCIAL_FIELDS.map((s) => {
          const Icon = s.icon;
          return (
            <Field key={s.key} label={s.label} testId={`sigmgr-social-${s.key}`}>
              <div style={{ position: "relative" }}>
                <Icon style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "#999" }} />
                <input
                  data-testid={`sigmgr-social-${s.key}-input`}
                  value={social[s.key] || ""}
                  onChange={(e) => setSocial({ ...social, [s.key]: e.target.value })}
                  placeholder={s.placeholder}
                  className="input"
                  style={{ ...INPUT, paddingLeft: 32 }}
                />
              </div>
            </Field>
          );
        })}

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button data-testid="sigmgr-save" onClick={save} disabled={saving} className="btn btn-primary" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            {saving ? <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> : <Save style={{ width: 14, height: 14 }} />}
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button data-testid="sigmgr-reset" onClick={resetToDefaults} disabled={resetting} className="btn" style={{ display: "inline-flex", gap: 6, alignItems: "center", marginLeft: "auto" }}>
            {resetting ? <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> : <RotateCcw style={{ width: 14, height: 14 }} />}
            Reset to defaults
          </button>
        </div>
      </div>

      {/* ── RIGHT: live preview ── */}
      <div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.4 }}>Live preview (your signature)</div>
        <div
          data-testid="sigmgr-preview"
          style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 18, minHeight: 200 }}
          dangerouslySetInnerHTML={{ __html: previewHtml || '<div style="color:#999;font-size:12px;">Set your name + title in <b>My Signature</b> to see a preview.</div>' }}
        />
        <p style={{ fontSize: 11, color: "#888", marginTop: 10 }}>
          The preview uses your personal signature (name/title/phone from <b>My Signature</b>) on top of these
          company-wide settings. Other users will see their own name/title with the same globals.
        </p>
      </div>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────
const INPUT = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 13,
  fontFamily: "inherit",
};

function Field({ label, hint, testId, children }) {
  return (
    <div data-testid={testId} style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 12, color: "#444", marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}
